import { ChangeEvent, FC, useContext, useEffect, useMemo, useState } from 'react';
import { PaperProps, Popper, PopperProps } from '@mui/material';
import { PaperComponent, PropsDiagnosesContext } from './DiagnosesInput.components';
import { DiagnosesSearchValueType } from '@src/api';
import { useDebounce } from '@src/hooks/useDebounce';
import { Autocomplete } from '@components/Autocomplete';
import { buildDataTestId } from '@src/utils';

interface DiagnosesInputProps {
  heightPoper?: number;
  scaleType?: DiagnosesSearchValueType;
  handleClickScaleType?: (val: DiagnosesSearchValueType) => void;
  isGroupsPage?: boolean;
  dataTestId?: string;
}

export const DiagnosesInput: FC<DiagnosesInputProps> = ({
  heightPoper = 530,
  handleClickScaleType,
  isGroupsPage,
  dataTestId,
}) => {
  const { scaleType } = useContext(PropsDiagnosesContext);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const resultDataTestId = buildDataTestId(dataTestId, 'input-diagnoses-search');
  useEffect(() => {
    setSearch('');
  }, [scaleType]);

  const handleCancel = () => {
    setOpen(false);
    setSearch('');
  };

  const customPopper = useMemo(
    () =>
      ({ children, ...popperProps }: PopperProps) => {
        return (
          <Popper
            sx={{
              height: `${heightPoper}px`,
              boxShadow: '0px 8px 12px rgba(55, 65, 81, 0.1), 0px 0px 6px rgba(55, 65, 81, 0.1)',
            }}
            {...popperProps}
            modifiers={[
              {
                name: 'flip',
                enabled: false,
              },
            ]}
            placement="bottom"
          >
            {children}
          </Popper>
        );
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleChange = (_e: ChangeEvent<HTMLInputElement>, value: string, reason) => {
    if (reason === 'blur' || reason === 'reset') return;
    setSearch(value || '');
  };
  const debouncedSearch = useDebounce(search, 500);
  return (
    <Autocomplete
      showCustomClearIcon
      renderOption={() => <></>}
      open={open}
      onClose={() => {}}
      PaperComponent={(props: PaperProps) => (
        <PaperComponent
          {...props}
          handleCancel={handleCancel}
          search={debouncedSearch}
          handleClickScaleType={handleClickScaleType}
          isGroupsPage={isGroupsPage}
          dataTestId={resultDataTestId}
        />
      )}
      PopperComponent={customPopper}
      setState={{}}
      onClick={() => setOpen(true)}
      componentsProps={{ popper: { sx: { zIndex: 1 } } }}
      placeholder="Search ICD-10 Code or Name"
      label="Diagnoses"
      options={[]}
      multiple={undefined}
      disabled={undefined}
      value={undefined}
      loading={undefined}
      disableClearable={true}
      onChange={() => {}}
      withSearchIcon
      sx={{ '& .MuiFormControl-root': { '& .MuiInputBase-root': { padding: '12px', input: { height: '18px' } } } }}
      onInputChange={handleChange}
      inputValue={search}
      dataTestId={resultDataTestId}
    />
  );
};
